import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, usd, formatApiErrorDetail } from "../../lib/api";
import { Plus, Trash2 } from "lucide-react";

function CodeRow({ c, onToggle, onView, onDelete }) {
  const s = c.stats;
  return (
    <tr data-testid={`referral-row-${c.code}`} className="border-t border-slate-100">
      <td className="px-4 py-3 font-mono font-bold text-[#03281B]">{c.code}</td>
      <td className="px-4 py-3">{c.partner}</td>
      <td className="px-4 py-3 font-semibold" data-testid={`referral-regs-${c.code}`}>{s.registrations}</td>
      <td className="px-4 py-3 hidden md:table-cell">{s.confirmed} / {s.pending} / {s.cancelled}</td>
      <td className="px-4 py-3 hidden md:table-cell">{s.participants}</td>
      <td className="px-4 py-3 font-semibold text-[#008057]" data-testid={`referral-revenue-${c.code}`}>{usd(s.revenue)}</td>
      <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-1 rounded-full ${c.active ? "bg-[#008057] text-white" : "bg-slate-200 text-slate-600"}`}>{c.active ? "Active" : "Inactive"}</span></td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button data-testid={`referral-view-${c.code}`} onClick={() => onView(c.code)} className="text-[#008057] font-semibold hover:underline mr-3">Registrations</button>
        <button data-testid={`referral-toggle-${c.code}`} onClick={() => onToggle(c)} className="text-slate-500 font-semibold hover:underline">{c.active ? "Deactivate" : "Activate"}</button>
        {s.registrations === 0 && <button data-testid={`referral-delete-${c.code}`} onClick={() => onDelete(c.code)} title="Delete code" className="text-red-500 hover:text-red-700 ml-3 align-middle"><Trash2 size={16} /></button>}
      </td>
    </tr>
  );
}

function NewCodeForm({ onCreated }) {
  const [code, setCode] = useState("");
  const [partner, setPartner] = useState("");
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/referral-codes", { code, partner }); toast.success("Referral code created"); setCode(""); setPartner(""); onCreated(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const cls = "rounded-xl border-2 border-[#E2EBF0] px-3 py-2.5 text-sm focus:border-[#008057] outline-none";
  return (
    <form onSubmit={create} className="flex flex-wrap gap-3 mt-4 rounded-2xl bg-white border border-[#E2EBF0] p-4">
      <input data-testid="new-referral-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, ""))} placeholder="Code (e.g. PARTNER2027)" className={`${cls} font-mono uppercase flex-1 min-w-[180px]`} required />
      <input data-testid="new-referral-partner" value={partner} onChange={(e) => setPartner(e.target.value)} placeholder="Club / partner name" className={`${cls} flex-1 min-w-[180px]`} required />
      <button data-testid="new-referral-submit" type="submit" className="rounded-xl bg-[#008057] text-white px-5 py-2.5 font-bold text-sm flex items-center gap-1"><Plus size={16} /> Add Code</button>
    </form>
  );
}

export function ReferralsPanel({ onViewRegistrations }) {
  const [codes, setCodes] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/referral-codes"); setCodes(data.codes); }
    catch (e) { toast.error("Couldn't load referral codes. Please retry."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (c) => {
    try { await api.patch(`/admin/referral-codes/${c.code}`, { active: !c.active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete referral code ${code}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/referral-codes/${code}`); toast.success("Referral code deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="referrals-panel">
      <h1 className="font-display text-3xl font-extrabold text-[#03281B]">Referral Codes</h1>
      <p className="text-sm text-slate-500 mt-1">Track registrations that came through clubs and partners. Share a direct link like <span className="font-mono">{window.location.origin}/register?ref=COMETS2027</span></p>
      <NewCodeForm onCreated={load} />
      <div className="mt-4 rounded-2xl bg-white border border-[#E2EBF0] overflow-x-auto">
        <table data-testid="referrals-table" className="w-full text-sm">
          <thead className="bg-[#F2F8F5] text-slate-600"><tr>
            <th className="text-left px-4 py-3">Code</th><th className="text-left px-4 py-3">Partner</th><th className="text-left px-4 py-3">Registrations</th><th className="text-left px-4 py-3 hidden md:table-cell">Confirmed / Pending / Cancelled</th><th className="text-left px-4 py-3 hidden md:table-cell">Campers</th><th className="text-left px-4 py-3">Confirmed Revenue</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {codes.map((c) => <CodeRow key={c.code} c={c} onToggle={toggle} onView={onViewRegistrations} onDelete={remove} />)}
            {codes.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No referral codes yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
